/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { AuthSession } from '../redux/auth.slice';

/**
 * Push the GA4 login event for a freshly written session. Call it right after
 * `writeSession` on a real login — not on profile saves or CSR customer
 * switches, which also go through `writeSession`.
 *
 * Registered accounts get `log_in` with `userId` (GTM's CD_user_ID) and the
 * legacy `user_id`. Guests get `guest_login` with no id: guest account ids are
 * minted per checkout and would pollute unique-user counts.
 *
 * Synchronous fire-and-forget. If a hard navigation unloads the page before GTM
 * has processed the push, the hit is lost; that rare loss is accepted. Do not
 * add `eventCallback`, timeouts, or other waits before navigating — the extra
 * complexity isn't worth the handful of hits it would recover.
 */
export function pushLogInEvent(session: AuthSession): void {
    const accountId = session.account?.account_id;
    if (!accountId || !Array.isArray(window.dataLayer)) {
        return;
    }

    if (session.is_guest) {
        window.dataLayer.push({ event: 'guest_login', login_type: 'guest' });
        return;
    }

    window.dataLayer.push({
        event: 'log_in',
        login_type: 'registered',
        userId: accountId,
        user_id: accountId,
    });
}
